import React from 'react';
import { Linkedin, Instagram, Mail, MapPin, Phone, ArrowRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const footerLinks = {
    services: [
      { name: 'AI Development', path: '/ai-development' },
      { name: 'AI Consulting', path: '/ai-consulting' },
      { name: 'Software Development', path: '/software-development' }
    ],
    company: [
      { name: 'About', path: '/about' },
      { name: 'Resources', path: '/resources' },
      { name: 'Contact', path: '#contact' }
    ],
    initiative: [
      { name: 'ClassIQ', path: 'https://classiq.thethinkverse.ai', external: true }
    ]
  };

  return (
    <footer className="bg-slate-50 border-t border-slate-200 pt-20 pb-10 relative overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#3b82f6 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>

      {/* Top Gradient Line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent"></div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">
          {/* Brand Section - Spans 4 columns */}
          <div className="lg:col-span-4 space-y-8">
            <Link to="/" className="flex items-center gap-3 group w-fit">
              <div className="relative flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-100 to-purple-100 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_intelligent-home-1/artifacts/w793r8mv_ThinkVerse.AI%20%282%29.png"
                  alt="ThinkVerse.AI"
                  className="h-11 w-auto relative z-10 transition-transform duration-700 group-hover:scale-105 group-hover:rotate-[2deg]"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-slate-900 font-bold text-2xl tracking-tight leading-none group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-500">
                  ThinkVerse.AI
                </span>
              </div>
            </Link>

            <p className="text-slate-600 text-sm leading-relaxed max-w-sm font-medium">
              Transforming complex business challenges into intelligent, scalable solutions through advanced AI and software engineering.
            </p>

            <div className="space-y-4 pt-2">
              <div className="flex items-start gap-3 text-sm text-slate-600 group">
                <div className="p-2 rounded-lg bg-white border border-slate-100 shadow-sm group-hover:border-blue-100 group-hover:shadow-blue-50 transition-all duration-300">
                  <MapPin className="w-4 h-4 text-blue-600" />
                </div>
                <span className="mt-1.5">H No. 43 Sukhsagar Phase 4, Bhanpur,<br />Bhopal, MP 462022, India</span>
              </div>
              <a href="mailto:info@thethinkverse.ai" className="flex items-center gap-3 text-sm text-slate-600 hover:text-blue-600 transition-colors w-fit group">
                <div className="p-2 rounded-lg bg-white border border-slate-100 shadow-sm group-hover:border-blue-100 group-hover:shadow-blue-50 transition-all duration-300">
                  <Mail className="w-4 h-4 text-blue-600" />
                </div>
                <span className="font-medium">info@thethinkverse.ai</span>
              </a>
              <a href="tel:+918962386569" className="flex items-center gap-3 text-sm text-slate-600 hover:text-blue-600 transition-colors w-fit group">
                <div className="p-2 rounded-lg bg-white border border-slate-100 shadow-sm group-hover:border-blue-100 group-hover:shadow-blue-50 transition-all duration-300">
                  <Phone className="w-4 h-4 text-blue-600" />
                </div>
                <span className="font-medium">+91 89623 86569</span>
              </a>
            </div>
          </div>

          {/* Links Sections - Spans 8 columns total */}
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-10 lg:pl-12 pt-2">
            {/* Services */}
            <div>
              <h3 className="text-slate-900 font-bold text-sm uppercase tracking-wider mb-6">Services</h3>
              <ul className="space-y-4">
                {footerLinks.services.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-slate-600 hover:text-blue-600 text-sm font-medium transition-all duration-300 flex items-center gap-2 group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-blue-600 group-hover:scale-125 transition-all duration-300"></span>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-slate-900 font-bold text-sm uppercase tracking-wider mb-6">Company</h3>
              <ul className="space-y-4">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    {link.path.startsWith('#') ? (
                      <a
                        href={link.path}
                        className="text-slate-600 hover:text-blue-600 text-sm font-medium transition-all duration-300 flex items-center gap-2 group"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-blue-600 group-hover:scale-125 transition-all duration-300"></span>
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.path}
                        className="text-slate-600 hover:text-blue-600 text-sm font-medium transition-all duration-300 flex items-center gap-2 group"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-blue-600 group-hover:scale-125 transition-all duration-300"></span>
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h3 className="text-slate-900 font-bold text-sm uppercase tracking-wider mb-6">Connect</h3>
              <div className="space-y-8">
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Initiatives</p>
                  <ul className="space-y-4">
                    {footerLinks.initiative.map((link) => (
                      <li key={link.name}>
                        <a
                          href={link.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-600 hover:text-blue-600 text-sm font-medium transition-all duration-300 flex items-center gap-2 group"
                        >
                          {link.name}
                          <ExternalLink className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Social</p>
                  <div className="flex gap-3">
                    <a
                      href="https://www.linkedin.com/company/108632549"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-600 hover:text-blue-600 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-100 hover:-translate-y-1 transition-all duration-300"
                      aria-label="LinkedIn"
                    >
                      <Linkedin size={18} />
                    </a>
                    <a
                      href="https://www.instagram.com/thinkverse.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-600 hover:text-pink-600 hover:border-pink-200 hover:shadow-lg hover:shadow-pink-100 hover:-translate-y-1 transition-all duration-300"
                      aria-label="Instagram"
                    >
                      <Instagram size={18} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm font-medium">
            © {new Date().getFullYear()} ThinkVerse.AI. All rights reserved.
          </p>
          <div className="flex items-center gap-8 text-sm text-slate-500 font-medium">
            <Link to="/privacy" className="hover:text-blue-600 transition-colors relative group">
              Privacy Policy
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-blue-600 group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link to="/terms" className="hover:text-blue-600 transition-colors relative group">
              Terms of Service
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-blue-600 group-hover:w-full transition-all duration-300"></span>
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
